import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useDeleteScheduledMessage,
  useSendScheduleMessageToApi,
  useUpsertMessageTemplate,
} from "../../../App/Queries/MessageQueries";
import { useMessageInputState } from "../../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import {
  useMessageTemplateHandlers,
  useMessageTemplateState,
} from "../../../App/State/Hooks/MessageDialog/useMessageTemplate";
import { useAsyncDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { IGroupMessageView, IMessageScheduleQueue, IMessageSendPost } from "../../../App/Typings/MessageText";

export type SendMessagesMutationState = ReturnType<typeof useMessageMutationState>;

export default function useMessageMutationState(sendMessageBody: IMessageSendPost) {
  const { pathname } = useLocation();
  const [tempMessages, setTempMessages] = useState<IMessageScheduleQueue[]>([]);

  const { mutateAsync: postSendMessage, isLoading: postSendMessageIsLoading } = useSendScheduleMessageToApi();
  const { mutateAsync: upsertMessageTemplate, isLoading: upsertMessageTemplateLoading } =
    useUpsertMessageTemplate();
  const { mutateAsync: deleteScheduledMessage } = useDeleteScheduledMessage();

  const { msgListQueue } = useMessageScheduleState();
  const { body, subject } = useMessageInputState();
  const { name: templateName, visibility } = useMessageTemplateState();

  const { handleClearScheduleOptions, handleReplaceMessageQueue } = useMessageScheduleHandlers();
  const { handleValidateTemplateState, handleClearTemplateInputState } = useMessageTemplateHandlers();

  const disableSendMessageMutation = !msgListQueue.length;
  const disableSaveTemplateMutation = !handleValidateTemplateState();

  const handleScheduleMessages = useAsyncDebouncedHandler(async () => {
    if (postSendMessageIsLoading || disableSendMessageMutation) {
      return;
    }
    await handleSendMessages(msgListQueue);
  }, 300);

  const handleSendMessages = useAsyncDebouncedHandler(async (msgListQueue: IMessageScheduleQueue[]) => {
    if (!msgListQueue.length) {
      return;
    }

    setTempMessages(msgListQueue.filter((c) => !c.scheduled));

    await postSendMessage(
      {
        ...sendMessageBody,
        messages: msgListQueue,
        uiComponent: pathname,
      },
      {
        onError: (_, variables) => {
          handleReplaceMessageQueue(variables.messages);
        },
        onSettled: () => {
          handleClearScheduleOptions();
        },
      },
    );
  }, 300);

  const handleSaveEmailTemplate = useAsyncDebouncedHandler(async () => {
    if (upsertMessageTemplateLoading || disableSaveTemplateMutation) {
      return;
    }
    await upsertMessageTemplate(
      {
        name: templateName,
        visibility: visibility,
        body,
        subject,
        uiComponent: pathname,
        type: "email",
      },
      {
        onSuccess: () => {
          handleClearTemplateInputState();
        },
      },
    );
  }, 300);

  const handleSaveTextTemplate = useAsyncDebouncedHandler(async () => {
    if (upsertMessageTemplateLoading || disableSaveTemplateMutation) {
      return;
    }
    await upsertMessageTemplate(
      {
        name: templateName,
        visibility: visibility,
        body,
        uiComponent: pathname,
        type: "text",
      },
      {
        onSuccess: () => {
          handleClearTemplateInputState();
        },
      },
    );
  }, 300);

  const handleConfirmDeleteSelectedScheduledMessages = useAsyncDebouncedHandler(
    async (msg: IGroupMessageView) => await deleteScheduledMessage(msg.messageTrackingId),
    300,
  );

  return {
    handleSaveEmailTemplate,
    handleSaveTextTemplate,
    handleSendMessages,
    handleConfirmDeleteSelectedScheduledMessages,
    handleScheduleMessages,
    postSendMessageIsLoading,
    upsertMessageTemplateLoading,
    tempMessages,
    setTempMessages,
  };
}
